<template>
  <div class="flex flex-column gap-3" data-testid="project-shift-auths-view-auth-list">
    <p v-if="actionsBlocked">This violation must be resolved by the associate’s facility administrator.</p>
    <div class="item" v-if="authsList[current]" data-testid="project-shift-auths-view-auth-item">
      <div class="flex justify-content-between align-items-center">
        <div class="flex gap-2 align-items-center">
          <a-checkbox v-if="
            hasUncontrolledPermission('manage_authorizations', {
              id: associate.id
            }) && !actionsBlocked
          " @change="handleSelectChange(authsList[current].id)"
            :checked="!!selectedAuths.find(auth => authsList[current].id === auth)"
            data-testid="project-shift-auths-view-auth-checkbox"></a-checkbox>
          <h4 class="m-0">
            {{ convertType(authsList[current].type, authsList[current].status) }}
          </h4>
          <a-tooltip class="w-fit" v-if="authsList[current].confirmation">
            <template slot="title">
              {{ getConfirmationStatusText(authsList[current]) }}
            </template>
            <a-icon width="16" height="16" :style="{
              color: authsList[current].confirmation.status === 'accept' ? '#10b981' : '#ef4444',
              minWidth: '16px'
            }" :icon="authsList[current].confirmation.status === 'accept' ? 'carbon:checkmark-filled' : 'carbon:close-filled'
                " />
          </a-tooltip>
        </div>
        <a-button v-if="
          hasUncontrolledPermission('manage_authorizations', {
            id: associate.id
          }) && !actionsBlocked
        " type="link" class="p-0" :style="{ color: '#fa7e73' }" @click="deleteAuthConfirm(authsList[current].id)"
          data-testid="project-shift-auths-view-auth-delete-button">Delete</a-button>
      </div>
      <a-descriptions :column="1" size="small" class="col-fixed p-0" :colon="false"
        data-testid="project-shift-auths-view-auth-descriptions">
        <a-descriptions-item label="Created At">
          <div class="flex flex-column gap-2">
            {{ getDateTime(authsList[current].created_at) }}
            <a-tag class="m-0" color="#f59e0b" v-if="getSubmittedVersion(authsList[current].created_at)">
              Shift Version wasn’t Submitted
            </a-tag>
          </div>
        </a-descriptions-item>
        <a-descriptions-item label="Status">
          <a-tag class="m-0" :color="getStatusColor(authsList[current])">
            {{ getStatusText(authsList[current]) }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="Type">
          <a-tag class="m-0" color="#3b82f6">
            {{ convertType(authsList[current].type, authsList[current].status) }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="Explanation">
          {{ authsList[current].details ?? '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="Emergency" v-if="authsList[current].emergency !== null">
          {{ authsList[current].emergency ? 'Yes' : 'No' }}
        </a-descriptions-item>
        <a-descriptions-item label="Provider">
          <template v-if="authsList[current].provider">
            {{ authsList[current].provider.first_name }}
            {{ authsList[current].provider.last_name }}
          </template>
          <template v-else> - </template>
        </a-descriptions-item>
        <a-descriptions-item label="History">
          <history-tags :authorization="authsList[current]" :associate="associate" :is-detail-auth="true" />
        </a-descriptions-item>
        <a-descriptions-item label="Disciplinary Action">
          <div class="flex flex-wrap gap-2 justify-content-center"
            v-if="authsList[current].disciplinary_objects.length">
            <a-tag class="cursor-pointer m-0" color="#6b7280" v-for="object in authsList[current].disciplinary_objects"
              :key="object.object_id">
              {{ AUTH_DISCIPLINARY_ACTIONS[object.model_name] }}
            </a-tag>
          </div>
          <span v-else>-</span>
        </a-descriptions-item>
        <template v-if="authsList[current].status !== 'initial'">
          <a-descriptions-item label="Resolved By"> {{ getReviewedByName(authsList[current]) }}
          </a-descriptions-item><a-descriptions-item label="Resolve Date">
            {{
              authsList[current].review_date ? getDateTime(authsList[current].review_date) : '-'
            }} </a-descriptions-item><a-descriptions-item
            :label="authsList[current].status === 'accepted' ? `Comment` : 'Declined Reason'">
            {{ authsList[current].review_comment ?? '-' }}
          </a-descriptions-item>
        </template>
        <a-descriptions-item label="Attachments">
          <attachments-field v-if="authsList[current].files.length" :files="authsList[current].files" :is-view="true"
            :with-title="false" />
          <span v-else>-</span>
        </a-descriptions-item>
      </a-descriptions>
    </div>
    <a-pagination :current="current + 1" :total="authsList.length" :page-size="1" @change="onPageChange"
      data-testid="project-shift-auths-view-auth-pagination" />
  </div>
</template>

<script>
import { Button, Checkbox, Descriptions, notification, Pagination, Tag, Tooltip } from 'ant-design-vue'
import HistoryTags from '@/modules/authorizations/components/shift-auth-history.vue'
import shiftAuths from '@/helpers/shift-auths'
import Util from '@/util'
import ShiftFormAttachments from '../../../../components/form-attachments.vue'
import { createNamespacedHelpers } from 'vuex'
import { AUTH_DISCIPLINARY_ACTIONS, AUTH_PROCESS_STATUSES } from '@/store/modules/shifts/constants'
import moment from 'moment-timezone'
import permissionMixin from '@/mixins/permissionMixin'

const { mapActions } = createNamespacedHelpers('shifts')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-button': Button,
    'a-tag': Tag,
    'a-tooltip': Tooltip,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-checkbox': Checkbox,
    'a-pagination': Pagination,
    'attachments-field': ShiftFormAttachments,
    'history-tags': HistoryTags
  },
  props: {
    authsList: {
      type: Array,
      default: () => []
    },
    selectedAuths: {
      type: Array,
      default: () => []
    },
    associate: {
      type: Object,
      default: () => { }
    },
    versionCreated: {
      type: Boolean,
      default: true
    },
    initialPage: {
      type: Number,
      default: 0
    },
    actionsBlocked: {
      type: Boolean,
      default: false
    }
  },
  mixins: [permissionMixin],
  data() {
    return {
      current: 0,

      AUTH_DISCIPLINARY_ACTIONS
    }
  },
  watch: {
    initialPage: {
      handler: 'syncCurrentPage',
      immediate: true
    },
    authsList() {
      this.syncCurrentPage(this.initialPage)
    }
  },
  methods: {
    ...mapActions(['deleteAuthorization']),
    ...modalActions(['openModal']),

    convertType: shiftAuths.convertType,

    syncCurrentPage(page) {
      const lastPage = Math.max(this.authsList.length - 1, 0)
      this.current = Math.min(Math.max(page || 0, 0), lastPage)
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    onPageChange(current) {
      this.current = current - 1
    },

    handleSelectChange(id) {
      const index = this.selectedAuths.indexOf(id)
      if (index > -1) {
        this.selectedAuths.splice(index, 1)
      } else {
        this.selectedAuths.push(id)
      }
    },

    getSubmittedVersion(created_at) {
      if (!this.versionCreated) return true

      const momentCreated = moment(created_at)
      const momentVersion = moment(this.versionCreated)

      return momentCreated.isBefore(momentVersion)
    },

    getStatusText(record) {
      if (record.deleted_at && record.status === 'declined' && record.resolved_by_associate) {
        return 'Declined & Resolved'
      }

      return AUTH_PROCESS_STATUSES[record.status]
    },
    getStatusColor(record) {
      if (record.deleted_at && record.status === 'declined' && record.resolved_by_associate) {
        return '#10b981'
      }

      switch (record.status) {
        case 'accepted':
          return '#10b981'
        case 'declined':
          return '#ef4444'
        default:
          return '#6b7280'
      }
    },
    getReviewedByName(record) {
      if (!record.reviewed_by) {
        return '-'
      }

      return Util.combineAssociateName(record.reviewed_by.first_name, record.reviewed_by.last_name)
    },
    getDateTime(date) {
      return Util.convertDateTimeToCurrentTimeZoneFormattedString(date)
    },

    convertConfirmationStatus(confirmation) {
      return Util.capitalize(confirmation.status)
    },

    getConfirmationStatusText(auth) {
      switch (auth.confirmation.status) {
        case 'accept':
          return `${auth.confirmation.author.first_name} ${auth.confirmation.author.last_name} confirmed that they allowed a violation of the company policy.`
        case 'decline':
          return `${auth.confirmation.author.first_name} ${auth.confirmation.author.last_name} didn't confirm that they didn't allow a violation of the company policy.`
        default:
          return ''
      }
    },
    deleteAuthConfirm(id) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Delete Violation',
          description: 'This violation will be deleted permanently.',
          onConfirm: async () => {
            await this.deleteAuthorization({ id })

            this.showNotification('success', 'Info', 'Violation has been deleted successfully')

            this.$emit('delete', id)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.modal-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  max-height: 350px;
}

.item {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius);
}
</style>
